/**
 * Featured collection slider tabs. First tab is Liquid. Other tabs load on demand.
 * Loaded from sections/featured-collection-slider.liquid as type="module".
 */

const SELECTOR = '[data-featured-collection-slider]'
const slideFetches = new Map()

function fetchSlides(url) {
  let request = slideFetches.get(url)
  if (!request) {
    request = (async () => {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })

      if (!response.ok) throw new Error(`Unable to load featured collection products (${response.status})`)

      const html = await response.text()
      const documentFragment = new DOMParser().parseFromString(html, 'text/html')
      const container = documentFragment.querySelector('[data-featured-collection-deferred-slides]')
      if (!container) throw new Error('Deferred featured collection response did not contain product slides.')

      return {
        productCount: Number.parseInt(container.dataset.productCount || '0', 10) || 0,
        slides: [...container.children].map((slide) => document.importNode(slide, true)),
      }
    })()
    slideFetches.set(url, request)
  }

  return request.then(({ productCount, slides }) => ({
    productCount,
    slides: slides.map((slide) => slide.cloneNode(true)),
  }))
}

function init(root) {
  if (root.dataset.sliderReady) return
  root.dataset.sliderReady = 'true'

  const sectionId = root.dataset.sectionId
  const tabs = [...root.querySelectorAll('[data-featured-collection-slider-tab]')]
  const panels = [...root.querySelectorAll('[data-featured-collection-slider-panel]')]
  const prevButton = root.querySelector('[data-featured-collection-slider-prev]')
  const nextButton = root.querySelector('[data-featured-collection-slider-next]')
  const panelLoads = new WeakMap()
  const deferredSliders = new Set()
  let selectionRequest = 0
  let activeTabIndex = tabs[0]?.dataset.tabIndex || panels[0]?.dataset.tabIndex || '0'

  const getActiveSliderElement = () => {
    const activePanel =
      panels.find((panel) => panel.dataset.tabIndex === activeTabIndex) || panels[0]
    return activePanel?.querySelector('[data-featured-collection-slider-slider]') || null
  }

  const getSlides = (sliderElement) =>
    [...sliderElement.querySelectorAll('.aero-slider__slide:not([data-aero-slider-clone])')]

  const getSlidesPerView = (sliderElement) => {
    const value = parseFloat(
      getComputedStyle(sliderElement).getPropertyValue('--slides-per-view').trim()
    )
    return Number.isFinite(value) && value > 0 ? value : 1
  }

  // Left-aligned integer SPV uses floor(count - SPV). Fractional SPV (the 1.05
  // peek) switches aero-slider to maxIndex = count - 1, so next() walks into
  // indexes that all clamp to the same end scroll. Cap at the last distinct
  // index and let prev()/next() step by one.
  const getMaxIndex = (sliderElement, slider) => {
    const slideCount = slider.slideCount ?? getSlides(sliderElement).length
    return Math.max(0, Math.ceil(slideCount - getSlidesPerView(sliderElement)))
  }

  const syncNavState = () => {
    if (!prevButton || !nextButton) return

    const sliderElement = getActiveSliderElement()
    const slider = sliderElement?.aeroSlider
    if (!sliderElement || !slider) {
      prevButton.disabled = true
      nextButton.disabled = true
      return
    }

    const index = slider.currentIndex ?? 0
    const maxIndex = getMaxIndex(sliderElement, slider)

    prevButton.disabled = index <= 0
    nextButton.disabled = index >= maxIndex
  }

  const setActiveTab = (tabIndex, { focusTab = false } = {}) => {
    activeTabIndex = String(tabIndex)

    tabs.forEach((tab) => {
      const isActive = tab.dataset.tabIndex === activeTabIndex
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false')
      tab.tabIndex = isActive ? 0 : -1
      if (focusTab && isActive) tab.focus()
    })

    panels.forEach((panel) => {
      const isActive = panel.dataset.tabIndex === activeTabIndex
      panel.classList.toggle('is-inactive', !isActive)
      if (isActive) panel.removeAttribute('inert')
      else panel.setAttribute('inert', '')
    })

    syncNavState()
  }

  const getDeferredUrls = (panel) => {
    if (panel.dataset.deferredUrl) return [panel.dataset.deferredUrl]

    const productUrls = panel.querySelector('[data-featured-collection-slider-product-urls]')
    if (!productUrls) return []

    try {
      const urls = JSON.parse(productUrls.textContent || '[]')
      return Array.isArray(urls) ? urls.filter(Boolean) : []
    } catch (error) {
      console.error('Unable to parse deferred featured collection product URLs.', error)
      return []
    }
  }

  const preloadDeferredPanels = () => {
    panels.forEach((panel) => {
      if (panel.dataset.panelLoaded === 'true') return
      getDeferredUrls(panel).forEach((url) => {
        fetchSlides(url).catch(() => {})
      })
    })
  }

  const applyPanelSettings = (slides) => {
    const hidePrice = root.dataset.hidePrice === 'true'
    const hideQuickView = root.dataset.hideQuickView === 'true'
    const desktopColumns = root.dataset.productColumns || '4'
    const mobileColumns = root.dataset.productColumnsMobile === '1' ? '1' : '2'
    const mobileSize = `calc((90vw - 12px) / ${mobileColumns})`
    const desktopSize = desktopColumns === '5'
      ? 'calc((93.4vw - (12px * 3)) / 5)'
      : desktopColumns === '3'
        ? 'calc((93.4vw - (12px * 3)) / 3)'
        : 'calc((93.4vw - (12px * 3)) / 4)'
    const cappedDesktopSize = desktopColumns === '5'
      ? 'calc((1400px * 0.934 - (12px * 3)) / 5)'
      : desktopColumns === '3'
        ? 'calc((1400px * 0.934 - (12px * 3)) / 3)'
        : 'calc((1400px * 0.934 - (12px * 3)) / 4)'
    const imageSizes = `(max-width: 720px) ${mobileSize}, (max-width: 1400px) ${desktopSize}, ${cappedDesktopSize}`

    slides.forEach((slide, index) => {
      const productItem = slide.querySelector('.product-item')
      if (productItem) {
        productItem.classList.toggle('first-item', index === 0)
        productItem.style.setProperty('--z-index-item', String(index + 1))
      }

      if (hidePrice) {
        slide.querySelectorAll('.product-item__price').forEach((price) => price.remove())
      }

      if (hideQuickView) {
        slide.querySelectorAll('.product-item__hover-action-wrap').forEach((action) => action.remove())
      }

      slide.querySelectorAll('.product-item img[sizes]').forEach((image) => {
        image.setAttribute('sizes', imageSizes)
      })
    })
  }

  const buildSlider = (panel, slides, productCount) => {
    const maxProducts = Number.parseInt(panel.dataset.maxProducts || '15', 10) || 15
    const limitedSlides = slides.slice(0, maxProducts)
    applyPanelSettings(limitedSlides)

    const sliderElement = document.createElement('div')
    sliderElement.className = 'featured-collection-slider__slider aero-slider relative w-full min-w-0 max-w-none'
    sliderElement.dataset.featuredCollectionSliderSlider = ''
    sliderElement.dataset.aeroAlignment = 'left'

    const viewport = document.createElement('div')
    viewport.className = 'aero-slider__viewport aero-slider__viewport--bleed-right featured-collection-slider__viewport'

    const track = document.createElement('div')
    track.className = 'aero-slider__track'
    limitedSlides.forEach((slide) => track.appendChild(slide))

    const viewAllTemplate = panel.querySelector('[data-featured-collection-slider-view-all]')
    if (viewAllTemplate && productCount > maxProducts) {
      track.appendChild(viewAllTemplate.content.cloneNode(true))
    }

    viewport.appendChild(track)
    sliderElement.appendChild(viewport)
    panel.replaceChildren(sliderElement)
    return sliderElement
  }

  const renderLoadError = (panel) => {
    const fallbackElement = panel.querySelector('[data-featured-collection-slider-fallback-url]')
    const fallbackUrl = panel.dataset.fallbackUrl || fallbackElement?.textContent?.trim() || '/collections/all-products'
    const message = document.createElement('p')
    message.className = 'm-0 py-10 text-base'
    message.textContent = 'Products could not be loaded. '

    const link = document.createElement('a')
    link.href = fallbackUrl
    link.className = 'underline underline-offset-4'
    link.textContent = 'View all products'
    message.appendChild(link)
    panel.replaceChildren(message)
  }

  const initializeDeferredSlider = (sliderElement, panel) => {
    const slider = sliderElement.aeroSlider || window.AeroSlider.createSlider(sliderElement, {
      alignment: 'left',
      draggable: true,
      loop: false,
      autoplay: false,
    })

    deferredSliders.add(slider)
    sliderElement.addEventListener('aero:slideChange', syncNavState)
    document.dispatchEvent(new CustomEvent('product-item:reinit', { detail: { root: panel } }))
  }

  const loadPanel = (panel) => {
    if (panel.dataset.panelLoaded === 'true') return Promise.resolve()
    if (panelLoads.has(panel)) return panelLoads.get(panel)

    const load = (async () => {
      const urls = getDeferredUrls(panel)
      const tab = tabs.find((item) => item.dataset.tabIndex === panel.dataset.tabIndex)
      tab?.setAttribute('aria-busy', 'true')
      panel.setAttribute('aria-busy', 'true')

      try {
        if (!urls.length) throw new Error('No deferred product URL was configured for this tab.')

        const results = await Promise.all(urls.map(fetchSlides))
        const slides = results.flatMap((result) => result.slides)
        const productCount = panel.dataset.panelKind === 'product_list'
          ? slides.length
          : results[0]?.productCount || slides.length

        const sliderElement = buildSlider(panel, slides, productCount)
        panel.dataset.panelLoaded = 'true'
        await initializeDeferredSlider(sliderElement, panel)
      } catch (error) {
        console.error('Unable to load deferred featured collection tab.', error)
        renderLoadError(panel)
        panel.dataset.panelLoaded = 'true'
      } finally {
        tab?.removeAttribute('aria-busy')
        panel.removeAttribute('aria-busy')
      }
    })()

    panelLoads.set(panel, load)
    return load
  }

  const selectTab = async (tabIndex, { focusTab = false } = {}) => {
    const requestedIndex = String(tabIndex)
    const requestId = ++selectionRequest
    const tab = tabs.find((item) => item.dataset.tabIndex === requestedIndex)
    const panel = panels.find((item) => item.dataset.tabIndex === requestedIndex)
    if (!tab || !panel) return

    if (focusTab) tab.focus()
    await loadPanel(panel)
    if (requestId !== selectionRequest) return
    setActiveTab(requestedIndex, { focusTab })
  }

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      selectTab(tab.dataset.tabIndex)
    })

    tab.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
      event.preventDefault()

      const currentIndex = tabs.indexOf(tab)
      let nextIndex = currentIndex
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length
      if (event.key === 'Home') nextIndex = 0
      if (event.key === 'End') nextIndex = tabs.length - 1
      selectTab(tabs[nextIndex].dataset.tabIndex, { focusTab: true })
    })
  })

  root.addEventListener('click', (event) => {
    const isPrev = event.target.closest('[data-featured-collection-slider-prev]')
    const isNext = event.target.closest('[data-featured-collection-slider-next]')
    if (!isPrev && !isNext) return

    event.preventDefault()
    const sliderElement = getActiveSliderElement()
    const slider = sliderElement?.aeroSlider
    if (!slider) return

    const current = slider.currentIndex ?? 0
    const maxIndex = getMaxIndex(sliderElement, slider)

    if (isPrev && !prevButton.disabled) {
      if (current > maxIndex) slider.goTo(Math.max(0, maxIndex - 1))
      else slider.prev()
    }
    if (isNext && !nextButton.disabled) slider.next()
  })

  panels.forEach((panel) => {
    const sliderElement = panel.querySelector('[data-featured-collection-slider-slider]')
    if (!sliderElement) return
    sliderElement.addEventListener('aero:slideChange', syncNavState)
  })

  const bindNavWhenReady = () => {
    syncNavState()
    if (!getActiveSliderElement()?.aeroSlider) {
      window.setTimeout(bindNavWhenReady, 100)
    }
  }

  bindNavWhenReady()

  let preloadObserver
  if ('IntersectionObserver' in window) {
    preloadObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      preloadObserver.disconnect()
      preloadDeferredPanels()
    }, { rootMargin: '600px 0px' })
    preloadObserver.observe(root)
  } else {
    preloadDeferredPanels()
  }

  const onBlockSelect = (event) => {
    if (!event.detail || event.detail.sectionId !== sectionId) return
    const panel = event.target?.closest?.('[data-featured-collection-slider-panel]')
    if (!panel) return
    selectTab(panel.dataset.tabIndex)
  }

  document.addEventListener('shopify:block:select', onBlockSelect)

  const onSectionUnload = (event) => {
    if (!event.detail || event.detail.sectionId !== sectionId) return
    preloadObserver?.disconnect()
    deferredSliders.forEach((slider) => slider.destroy())
    deferredSliders.clear()
    delete root.dataset.sliderReady
    document.removeEventListener('shopify:block:select', onBlockSelect)
    document.removeEventListener('shopify:section:unload', onSectionUnload)
  }

  document.addEventListener('shopify:section:unload', onSectionUnload)

  const dispatch = () => {
    document.dispatchEvent(new CustomEvent('product-item:reinit', { detail: { root } }))
  }

  if (document.readyState === 'complete') dispatch()
  else document.addEventListener('DOMContentLoaded', dispatch, { once: true })
}

function initAll(scope = document) {
  scope.querySelectorAll(SELECTOR).forEach(init)
}

initAll()

document.addEventListener('shopify:section:load', (event) => {
  initAll(event.target)
})
